/**
 * FinancialReportsPage — P&L / Cash Flow / Outstanding.
 *
 * All three reports read from /api/finance/reports/* which is the single
 * source of truth (approved-only, multi-currency, live FX conversion).
 * The page respects the finance permissions matrix — buttons the caller
 * cannot use are hidden based on /api/finance/me/capabilities.
 */
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  DollarSign, TrendingUp, TrendingDown, Loader2,
  Download, AlertTriangle, Calendar, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatCurrency as fmtCurrency } from '../lib/currency';

const fmt = (v, cur = 'USD') => fmtCurrency(v, cur || 'USD');

export default function FinancialReportsPage() {
  const [tab, setTab] = useState('pnl');
  const [days, setDays] = useState(90);
  const [convertTo, setConvertTo] = useState('USD');
  const [granularity, setGranularity] = useState('week');

  // Finance capabilities (roles) — hide buttons the user can't use
  const { data: caps } = useQuery({
    queryKey: ['finance-me-caps'],
    queryFn: () => api.get('/finance/me/capabilities').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });
  const can = (p) => (caps?.permissions || []).includes(p);

  // Full currency list from the ecosystem
  const { data: currencyMeta } = useQuery({
    queryKey: ['currency-list-public'],
    queryFn: () => api.get('/currency/list').then(r => r.data).catch(() => null),
    staleTime: 5 * 60 * 1000,
  });
  const currencies = (currencyMeta?.currencies || []).map(c => c.code);

  const { data: pnl, isLoading: pnlLoading } = useQuery({
    queryKey: ['finance-pnl', days, convertTo],
    queryFn: () => api.get('/finance/reports/pnl', {
      params: { days, convert_to: convertTo },
    }).then(r => r.data),
    enabled: tab === 'pnl' && can('read_reports'),
  });

  const { data: cash, isLoading: cashLoading } = useQuery({
    queryKey: ['finance-cash-flow', days, granularity],
    queryFn: () => api.get('/finance/reports/cash-flow', {
      params: { days, granularity },
    }).then(r => r.data),
    enabled: tab === 'cashflow' && can('read_reports'),
  });

  const { data: outstanding, isLoading: outLoading } = useQuery({
    queryKey: ['finance-outstanding'],
    queryFn: () => api.get('/finance/reports/outstanding').then(r => r.data),
    enabled: tab === 'outstanding' && can('read_reports'),
  });

  // Cash-flow chart data (single-currency reshape for display)
  const cashChartData = useMemo(() => {
    if (!cash?.series) return [];
    const byPeriod = {};
    cash.series.forEach(r => {
      if (r.currency !== convertTo) return; // for chart, use one currency
      byPeriod[r.period] = { period: r.period, inflow: r.inflow, outflow: r.outflow, net: r.net };
    });
    return Object.values(byPeriod).sort((a, b) => a.period.localeCompare(b.period));
  }, [cash, convertTo]);

  if (!caps) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!can('read_reports')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
        <Card className="max-w-md">
          <CardContent className="p-6 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-semibold text-slate-900">Finance reports access required</p>
              <p className="text-sm text-slate-600 mt-1">
                Your account has roles: <span className="font-mono">{(caps.roles || []).join(', ') || 'none'}</span>. Ask a super-admin to grant the <span className="font-mono">read_reports</span> permission via <span className="font-mono">finance_officer</span>, <span className="font-mono">treasurer</span>, or <span className="font-mono">auditor</span> role.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6" data-testid="financial-reports-page">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FileText className="h-6 w-6 text-emerald-600" />
              Financial Reports
            </h1>
            <p className="text-sm text-slate-500">
              Single source of truth — approved income & expenses, multi-currency, live FX.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={convertTo} onChange={(e) => setConvertTo(e.target.value)} className="text-sm border rounded h-9 px-2 bg-white" data-testid="reports-convert-to">
              {currencies.length > 0 ? currencies.map(c => <option key={c} value={c}>{c}</option>) : <option value="USD">USD</option>}
            </select>
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} className="text-sm border rounded h-9 px-2 bg-white" data-testid="reports-range">
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
              <option value={365}>1 year</option>
              <option value={730}>2 years</option>
            </select>
            {can('export') && (
              <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="reports-print">
                <Download className="h-3.5 w-3.5 mr-1" /> Print / PDF
              </Button>
            )}
            <Badge variant="outline" data-testid="reports-role-badge">
              {caps.roles.join(' + ') || 'no finance role'}
            </Badge>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 sm:grid-cols-3 w-full max-w-md">
            <TabsTrigger value="pnl" data-testid="tab-pnl">P&amp;L</TabsTrigger>
            <TabsTrigger value="cashflow" data-testid="tab-cashflow">Cash Flow</TabsTrigger>
            <TabsTrigger value="outstanding" data-testid="tab-outstanding">Outstanding</TabsTrigger>
          </TabsList>

          {/* Profit & Loss */}
          <TabsContent value="pnl" className="mt-4 space-y-4">
            {pnlLoading ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : (
              <>
                {pnl?.converted && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard title={`Revenue (${pnl.converted.target_currency})`} value={fmt(pnl.converted.total_revenue, pnl.converted.target_currency)} colour="emerald" icon={<DollarSign className="h-4 w-4" />} testid="pnl-revenue" />
                    <StatCard title={`Expenses (${pnl.converted.target_currency})`} value={fmt(pnl.converted.total_expenses, pnl.converted.target_currency)} colour="rose" icon={<TrendingDown className="h-4 w-4" />} testid="pnl-expenses" />
                    <StatCard title={`Net Profit (${pnl.converted.target_currency})`} value={fmt(pnl.converted.net_profit, pnl.converted.target_currency)} colour={pnl.converted.net_profit >= 0 ? 'emerald' : 'rose'} icon={<TrendingUp className="h-4 w-4" />} testid="pnl-net" />
                    <StatCard title="Margin" value={`${pnl.converted.margin_pct}%`} colour="blue" icon={<TrendingUp className="h-4 w-4" />} testid="pnl-margin" />
                  </div>
                )}
                <Card>
                  <CardHeader><CardTitle className="text-base">Revenue by category</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full text-sm">
                      <thead className="text-xs text-slate-500 uppercase tracking-wide border-b">
                        <tr>
                          <th className="text-left py-2 px-3">Category</th>
                          <th className="text-right py-2 px-3">Amount</th>
                          <th className="text-right py-2 px-3">Cur</th>
                          <th className="text-right py-2 px-3">Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(pnl?.revenue || []).map((r, i) => (
                          <tr key={i} className="border-b last:border-0" data-testid={`pnl-revenue-row-${r.category}`}>
                            <td className="py-2 px-3 capitalize">{r.category.replace(/_/g, ' ')}</td>
                            <td className="py-2 px-3 text-right text-emerald-700 font-medium">{fmt(r.amount, r.currency)}</td>
                            <td className="py-2 px-3 text-right text-slate-400 text-xs">{r.currency}</td>
                            <td className="py-2 px-3 text-right text-slate-500">{r.count}</td>
                          </tr>
                        ))}
                        {(pnl?.revenue || []).length === 0 && <tr><td colSpan="4" className="p-6 text-center text-slate-400">No approved revenue in this window</td></tr>}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-base">Expenses by category</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full text-sm">
                      <thead className="text-xs text-slate-500 uppercase tracking-wide border-b">
                        <tr>
                          <th className="text-left py-2 px-3">Category</th>
                          <th className="text-right py-2 px-3">Amount</th>
                          <th className="text-right py-2 px-3">Cur</th>
                          <th className="text-right py-2 px-3">Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(pnl?.expenses || []).map((r, i) => (
                          <tr key={i} className="border-b last:border-0" data-testid={`pnl-expense-row-${r.category}`}>
                            <td className="py-2 px-3 capitalize">{r.category.replace(/_/g, ' ')}</td>
                            <td className="py-2 px-3 text-right text-rose-700 font-medium">{fmt(r.amount, r.currency)}</td>
                            <td className="py-2 px-3 text-right text-slate-400 text-xs">{r.currency}</td>
                            <td className="py-2 px-3 text-right text-slate-500">{r.count}</td>
                          </tr>
                        ))}
                        {(pnl?.expenses || []).length === 0 && <tr><td colSpan="4" className="p-6 text-center text-slate-400">No approved expenses in this window</td></tr>}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Cash Flow */}
          <TabsContent value="cashflow" className="mt-4 space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Granularity:</span>
              {['day', 'week', 'month'].map(g => (
                <button
                  key={g}
                  onClick={() => setGranularity(g)}
                  className={`text-xs px-3 py-1.5 rounded-full border ${g === granularity ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                  data-testid={`granularity-${g}`}
                >
                  {g}
                </button>
              ))}
            </div>
            {cashLoading ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Inflow vs Outflow ({convertTo})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={cashChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(v) => fmt(v, convertTo)} />
                        <Legend />
                        <Bar dataKey="inflow" fill="#10b981" name="Inflow" />
                        <Bar dataKey="outflow" fill="#ef4444" name="Outflow" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Running balance ({convertTo})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={(cash?.series || []).filter(s => s.currency === convertTo)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(v) => fmt(v, convertTo)} />
                        <Line type="monotone" dataKey="running_balance" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                    <div className="text-xs text-slate-500 mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span>Closing balance per currency:</span>
                      {Object.entries(cash?.closing_balance_by_currency || {}).map(([c, v]) => (
                        <span key={c} className="inline-flex items-center gap-1">
                          <Badge variant="outline">{c}</Badge>
                          <span>{fmt(v, c)}</span>
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Outstanding */}
          <TabsContent value="outstanding" className="mt-4 space-y-4">
            {outLoading ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card data-testid="outstanding-income-card">
                    <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4 text-emerald-600" />Outstanding Income ({outstanding?.income_row_count || 0})</CardTitle></CardHeader>
                    <CardContent className="p-0">
                      <AgeingTable rows={outstanding?.outstanding_income} ageing={outstanding?.income_ageing} testidPrefix="out-income" />
                    </CardContent>
                  </Card>
                  <Card data-testid="outstanding-expenses-card">
                    <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4 text-rose-600" />Outstanding Expenses ({outstanding?.expense_row_count || 0})</CardTitle></CardHeader>
                    <CardContent className="p-0">
                      <AgeingTable rows={outstanding?.outstanding_expenses} ageing={outstanding?.expense_ageing} testidPrefix="out-expense" isExpense />
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ title, value, colour, icon, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className={`flex items-center gap-2 text-${colour}-600 mb-1`}>
          {icon}
          <span className="text-xs uppercase tracking-wide font-medium">{title}</span>
        </div>
        <p className={`text-2xl font-bold text-${colour}-700`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function AgeingTable({ rows, ageing, testidPrefix, isExpense = false }) {
  if (!rows || rows.length === 0) {
    return <p className="p-6 text-center text-slate-400">Nothing outstanding — you&apos;re all caught up.</p>;
  }
  return (
    <>
      {/* Ageing summary */}
      <div className="p-3 border-b bg-slate-50/60">
        {Object.entries(ageing || {}).map(([cur, buckets]) => (
          <div key={cur} className="flex items-center gap-2 text-xs flex-wrap py-1">
            <Badge variant="outline">{cur}</Badge>
            {['0-30', '31-60', '61-90', '91+'].map(b => (
              <span key={b} className="inline-flex items-center gap-1">
                <span className="text-slate-500">{b}d:</span>
                <span className={`font-medium ${b === '91+' ? 'text-red-700' : b === '61-90' ? 'text-amber-700' : 'text-slate-800'}`}>
                  {fmtCurrency(buckets[b] || 0, cur, { decimals: 0 })}
                </span>
              </span>
            ))}
          </div>
        ))}
      </div>
      <table className="w-full text-xs">
        <thead className="text-slate-500 uppercase tracking-wide border-b">
          <tr>
            <th className="text-left py-2 px-3">Name</th>
            <th className="text-left py-2 px-3">{isExpense ? 'Vendor' : 'Payer'}</th>
            <th className="text-right py-2 px-3">Amount</th>
            <th className="text-right py-2 px-3">Age</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b last:border-0" data-testid={`${testidPrefix}-${r.id}`}>
              <td className="py-2 px-3 truncate max-w-[160px]">{r.name}</td>
              <td className="py-2 px-3 text-slate-500 truncate max-w-[140px]">{isExpense ? r.vendor : r.payer_name}</td>
              <td className={`py-2 px-3 text-right font-medium ${isExpense ? 'text-rose-700' : 'text-emerald-700'}`}>
                {fmtCurrency(r.amount, r.currency)}
              </td>
              <td className="py-2 px-3 text-right">
                <Badge variant="outline" className={r.age_bucket === '91+' ? 'text-red-700 border-red-300' : r.age_bucket === '61-90' ? 'text-amber-700 border-amber-300' : ''}>
                  {r.age_bucket}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
